import clientAxios from "../config";

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/**
 * Listado paginado de pagos de premios. Admin only.
 * params: { status, payment_method, search, date_from, date_to, per_page, page }
 */
const getPrizePayouts = (token, params = {}) =>
    clientAxios.get(`/api/prize-payouts`, { params, ...auth(token) });

/**
 * Listado de Winners con premio en dinero pendiente de iniciar pago.
 */
const getPendingWinners = (token, params = {}) =>
    clientAxios.get(`/api/prize-payouts/pending-winners`, { params, ...auth(token) });

/**
 * Pre-cálculo de retefuente para un monto bruto en centavos.
 * Body: { gross_amount }  → returns { gross, withholding, net, threshold_cents, ... }
 */
const calculateWithholding = (token, grossAmountCents) =>
    clientAxios.post(`/api/prize-payouts/calculate`, { gross_amount: grossAmountCents }, auth(token));

/**
 * Inicia un pago. Admin.
 * Body: { winner_uuid, gross_amount (centavos), payment_method,
 *         payment_reference?, payment_account_holder?, deadline_at?, notes? }
 */
const createPrizePayout = (token, data) =>
    clientAxios.post(`/api/prize-payouts`, data, auth(token));

const showPrizePayout = (token, uuid) =>
    clientAxios.get(`/api/prize-payouts/${uuid}`, auth(token));

/**
 * Edita un pago en curso (PENDING o SIGNED). Admin only.
 * Si edita un pago SIGNED y cambia campos sustantivos
 * (gross_amount, payment_method, payment_reference,
 * payment_account_holder, deadline_at), el backend resetea la firma:
 * el pago vuelve a PENDING y el ganador debe firmar de nuevo.
 *
 * Body (todos opcionales): { gross_amount, payment_method,
 *   payment_reference, payment_account_holder, deadline_at, notes }.
 */
const updatePrizePayout = (token, uuid, data) =>
    clientAxios.patch(`/api/prize-payouts/${uuid}`, data, auth(token));

/**
 * Firma del ganador (dueño del Winners). Body: { signature: dataUrl }.
 */
const signPrizePayout = (token, uuid, signatureDataUrl) =>
    clientAxios.post(`/api/prize-payouts/${uuid}/sign`, { signature: signatureDataUrl }, auth(token));

/**
 * Marca el pago como entregado. Admin.
 * Acepta dos formas:
 *  - data simple (objeto plano JSON) → método sin soporte adjunto.
 *  - FormData con `payment_proof` (File) y opcionalmente
 *    `payment_reference`, `payment_account_holder`, `notes`.
 *
 * Para métodos no-EFECTIVO (TRANSFERENCIA / NEQUI / DAVIPLATA) el
 * backend exige `payment_proof`. Pasar siempre FormData en esos casos.
 */
const payPrizePayout = (token, uuid, data = {}) => {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const config = {
        ...auth(token),
        ...(isFormData
            ? { headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' } }
            : {}),
    };
    return clientAxios.post(`/api/prize-payouts/${uuid}/pay`, data, config);
};

/**
 * Reemplaza el soporte bancario de un pago ya entregado (PAID).
 * Admin only. Recibe un File y lo envía como multipart/form-data.
 */
const updatePrizePayoutProof = (token, uuid, file) => {
    const fd = new FormData();
    fd.append('payment_proof', file);
    return clientAxios.post(`/api/prize-payouts/${uuid}/proof`, fd, {
        ...auth(token),
        headers: { ...auth(token).headers, 'Content-Type': 'multipart/form-data' },
    });
};

/**
 * Descarga el soporte bancario adjunto al pago. Admin o ganador dueño.
 * Devuelve un blob (responseType: 'blob') — el llamador resuelve el
 * mime type a partir de los headers de respuesta.
 */
const downloadPrizePayoutProof = (token, uuid) =>
    clientAxios.get(`/api/prize-payouts/${uuid}/proof`, {
        ...auth(token),
        responseType: 'blob',
    });

/**
 * Helper de UI: descarga el soporte bancario y dispara el "Save as"
 * del navegador. Determina la extensión a partir del Content-Type
 * que devuelve el backend (PDF / JPG / PNG).
 */
const triggerPrizePayoutProofDownload = async (token, uuid, filename) => {
    const response = await downloadPrizePayoutProof(token, uuid);
    const contentType = response.headers?.['content-type'] || 'application/octet-stream';
    const blob = new Blob([response.data], { type: contentType });
    const extFromMime = contentType.includes('pdf')
        ? 'pdf'
        : contentType.includes('png')
            ? 'png'
            : contentType.includes('jpeg') || contentType.includes('jpg')
                ? 'jpg'
                : 'bin';
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? `soporte-pago-${uuid}.${extFromMime}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Cancela un pago. Admin. Body: { reason }.
 */
const cancelPrizePayout = (token, uuid, reason) =>
    clientAxios.post(`/api/prize-payouts/${uuid}/cancel`, { reason }, auth(token));

/**
 * Descarga el PDF del comprobante. Disponible cuando el pago está
 * SIGNED o PAID. Devuelve un blob (responseType: 'blob').
 */
const downloadPrizePayoutPdf = (token, uuid) =>
    clientAxios.get(`/api/prize-payouts/${uuid}/pdf`, {
        ...auth(token),
        responseType: 'blob',
    });

/**
 * Helper de UI: descarga el comprobante y dispara el "Save as" del
 * navegador. Usar dentro de handlers de botón.
 */
const triggerPrizePayoutPdfDownload = async (token, uuid, filename) => {
    const response = await downloadPrizePayoutPdf(token, uuid);
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename ?? `comprobante-premio-${uuid}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export {
    getPrizePayouts,
    getPendingWinners,
    calculateWithholding,
    createPrizePayout,
    showPrizePayout,
    updatePrizePayout,
    signPrizePayout,
    payPrizePayout,
    cancelPrizePayout,
    downloadPrizePayoutPdf,
    triggerPrizePayoutPdfDownload,
    updatePrizePayoutProof,
    downloadPrizePayoutProof,
    triggerPrizePayoutProofDownload,
};
